// One month of the trips calendar: a six-week grid with a colored bar per trip
// spanning the days it covers.
//
// Purely presentational -- it draws whatever `calendar` (from
// TripCalendar.buildTripCalendar) hands it and reports clicks. All the
// arithmetic lives in that module, so this file is markup and nothing else.
// It has no breakpoint logic either: the page picks between this and the
// agenda, which keeps this component renderable in a test with no matchMedia.
//
// LAYOUT. One CSS grid per week -- seven columns, and one row for the day
// numbers plus one per lane. A bar is a grid ITEM placed with
// `grid-column: <start> / span <n>`, and each of the bar's own stop cells is a
// grid item inside it, so there is no absolute positioning and no pixel
// arithmetic anywhere. Three consequences worth knowing:
//
//   * The day-cell backdrops span every row, not just the day-number row.
//     Splitting days and bars into two sibling grids would leave the "today"
//     highlight stopping above the bars.
//   * The track lists are interpolated into `style` from JS rather than passed
//     as custom properties, because `repeat(var(--n), 1fr)` is not reliably
//     supported. Only the color travels as a custom property (the same trick
//     TaskSummary uses for --tint-rgb).
//   * The bar's own tracks are `minmax(0, 1fr)`, not `1fr`. `1fr` is
//     `minmax(auto, 1fr)`, whose floor is the content's min-content width -- so
//     with text in the cells a long name would widen its own column and pull
//     the bar out of line with the day numbers above it. `.trip-cal-week`
//     already uses `minmax(0, 1fr)`; this matches it.

import { tripCalendarBarLabel } from '../../fleet-shared/TripCalendar.mjs';
import { resolvePlaceName } from '../../fleet-shared/PlaceFields.mjs';
import { calendarColorVar } from './tripCalendarColors.mjs';

// What one bar actually draws: one cell per stop, holding where you are for
// the days that cell covers.
//
// A cell gets the gazetteer's SHORT name -- a calendar day column is the
// narrowest surface in the app -- but keeps the FULL stored name as its title.
// That is deliberately unlike `placeCell`, which sets a title only when the
// name was shortened: here truncation is driven by the width of a day, not by
// the gazetteer, so "Squirrel Cove Government Dock" (which the gazetteer never
// shortens) is exactly the case a hover has to rescue.
//
// A trip where NO leg recorded a destination would otherwise draw as an
// unexplained colored stripe, so the bar names itself instead -- one cell, no
// dividers. The trip name is not a place: it is never shortened (a trip called
// "Roche Harbor, San Juan" must not become "Roche Harbor") and carries no
// title. It is `some`, not per-cell: a trip where only some legs are named
// keeps its blank cells, which is honest -- a blank cell under a divider means
// no destination was recorded for that leg.
function stopCellsToDraw(segment, placeIndex) {
  const stops = segment.stops || [];
  if (!stops.some((stop) => stop.name)) {
    return [{ offset: 0, span: segment.span, text: segment.group?.name || '', divided: false }];
  }
  return stops.map((stop) => ({
    offset: stop.offset,
    span: stop.span,
    text: resolvePlaceName(stop.name, placeIndex) || '',
    title: stop.name || undefined,
    // Where one leg hands over to the next. Offset 0 is the bar's cap, never a
    // divider -- which is also why a stop landing on a week boundary draws no
    // line in either week.
    divided: stop.offset > 0,
  }));
}

function dayClassName(day) {
  const classes = ['trip-cal-day'];
  if (!day.inMonth) classes.push('trip-cal-day--outside');
  if (day.isToday) classes.push('trip-cal-day--today');
  return classes.join(' ');
}

function dayNumberClassName(day) {
  const classes = ['trip-cal-daynum'];
  if (!day.inMonth) classes.push('trip-cal-daynum--outside');
  if (day.isToday) classes.push('trip-cal-daynum--today');
  return classes.join(' ');
}

function barClassName(segment, todayIndex) {
  const classes = ['trip-cal-bar'];
  if (!segment.continuesBefore) classes.push('trip-cal-bar--starts');
  if (!segment.continuesAfter) classes.push('trip-cal-bar--ends');
  // A trip that has already happened recedes, so a mixed selection stays
  // readable without a second legend.
  if (todayIndex != null && segment.endIndex < todayIndex && !segment.continuesAfter) {
    classes.push('trip-cal-bar--past');
  }
  return classes.join(' ');
}

const CalendarMonth = ({ calendar, colorIndexFor, placeIndex, onSelectGroup }) => {
  if (!calendar) return null;

  return (
    <div className="trip-cal">
      <div className="trip-cal-headings" aria-hidden="true">
        {calendar.weekdayHeadings.map((name) => (
          <div key={name} className="trip-cal-heading">{name}</div>
        ))}
      </div>

      {calendar.weeks.map((week) => {
        const lanes = Math.max(1, week.laneCount);
        return (
          // A landmark per week rather than role="grid": a real grid role
          // obliges roving arrow-key navigation, which nothing here needs. The
          // bars are buttons and carry the whole story in their labels.
          <div
            key={week.startIndex}
            className="trip-cal-week"
            role="group"
            aria-label={`Week of ${week.label}`}
            style={{ gridTemplateRows: `auto repeat(${lanes}, var(--tp-cal-lane-h))` }}
          >
            {week.days.map((day, column) => (
              <div
                key={`bg-${day.index}`}
                className={dayClassName(day)}
                aria-hidden="true"
                style={{ gridColumn: column + 1, gridRow: `1 / span ${lanes + 1}` }}
              />
            ))}

            {week.days.map((day, column) => (
              <div
                key={`num-${day.index}`}
                className={dayNumberClassName(day)}
                style={{ gridColumn: column + 1, gridRow: 1 }}
              >
                {day.date}
              </div>
            ))}

            {week.segments.map((segment) => {
              const cells = stopCellsToDraw(segment, placeIndex);
              return (
                <button
                  key={`${segment.key}-${segment.startIndex}`}
                  type="button"
                  className={barClassName(segment, calendar.todayIndex)}
                  // A trip crossing a week boundary draws more than one bar.
                  // Only the first is a tab stop, so tabbing visits each trip
                  // once; browse mode still reads the rest as "continued".
                  tabIndex={segment.continuesBefore ? -1 : 0}
                  // The bar's whole story, including the trip's name -- which
                  // the cells no longer show, because the legend above the grid
                  // is color-coded and already carries it.
                  aria-label={tripCalendarBarLabel(segment.group, {
                    placeIndex,
                    continued: segment.continuesBefore,
                    stops: segment.stops,
                  })}
                  onClick={() => onSelectGroup?.(segment.group)}
                  style={{
                    gridColumn: `${segment.startColumn} / span ${segment.span}`,
                    gridRow: segment.laneIndex + 2,
                    gridTemplateColumns: `repeat(${segment.span}, minmax(0, 1fr))`,
                    '--trip-color': calendarColorVar(colorIndexFor?.(segment.key) ?? 0),
                  }}
                >
                  {cells.map((cell, i) => (
                    <span
                      key={cell.offset}
                      className={`trip-cal-bar__stop${cell.divided ? ' trip-cal-bar__stop--divided' : ''}`}
                      title={cell.title}
                      style={{
                        gridColumn: `${cell.offset + 1} / span ${cell.span}`,
                        gridRow: 1,
                      }}
                    >
                      {/* The chevrons live INSIDE the end cells rather than
                          overlaying the bar: as flex items the name ellipsises
                          around them, which needs no measurement. */}
                      {i === 0 && segment.continuesBefore && (
                        <i className="fa-solid fa-chevron-left trip-cal-bar__chevron" aria-hidden="true" />
                      )}
                      <span className="trip-cal-bar__stop-name">{cell.text}</span>
                      {i === cells.length - 1 && segment.continuesAfter && (
                        <i className="fa-solid fa-chevron-right trip-cal-bar__chevron" aria-hidden="true" />
                      )}
                    </span>
                  ))}
                </button>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

export default CalendarMonth;
